import React, { useState } from 'react';
import { ModalToggleState } from '../../models/actionStates';
import { ViewVariant } from '../../models/ShopifyVariant.model';
import { ColorSwatches } from '../../product_detail/components/ColorSwatches';
import { useRecoilValue } from 'recoil';
import { shopifyState, toViewPriceFnState, abTestState, environmentContextState } from '../../recoil/atoms';
import { productsState } from '../../recoil/selectors';
import { useSetModalState } from '../../hooks/actions/useModalState';
import { sizeImage } from '../../util';
import { discountForDisplay } from '../../discounts';
import styles from './ColorUpsellModal.module.scss';
import { SelectionType } from '../../enums/SelectionType.enum';
import ProductFormButton from '../../product_detail/components/ProductFormButton';
import { DivButton } from '../../root/components/DivButton';
import { Icon } from '../../icons/Icon';
import { Direction, useProductAssets } from '../../hooks/useProductAssets';

interface Props {
  variant: ViewVariant;
  selectionType: SelectionType;
}

export const ColorUpsellModal: React.FC<Props> = ({ variant, selectionType }) => {
  const { warehouseSaleSKUs } = useRecoilValue(environmentContextState);
  const products = useRecoilValue(productsState);
  const abTests = useRecoilValue(abTestState);
  const { timedSaleActive } = useRecoilValue(environmentContextState);
  const foundProduct = Object.values(products).find((p) => p.variants.map((v) => v.id).includes(variant.id));
  const foundVariants = foundProduct?.variants.filter(
    (v) => v.size === variant.size && v.available && v.fit === variant.fit && !warehouseSaleSKUs.includes(v.sku),
  );

  const [availableVariants] = useState<ViewVariant[]>(foundVariants ?? []);
  const [selectedVariant, setSelectedVariant] = useState<ViewVariant>(variant);
  const setModal = useSetModalState();

  const photos = useProductAssets(foundProduct);
  const filteredMannequinPhotos = photos.mannequinPhotos.filter(({ color, direction }) => color === selectedVariant?.color && direction === Direction.Front);
  const mainPhoto = filteredMannequinPhotos[0];

  const shopify = useRecoilValue(shopifyState);
  const toViewPriceFn = useRecoilValue(toViewPriceFnState);
  const discountText = discountForDisplay(2, toViewPriceFn, timedSaleActive, shopify, abTests);

  return (
    <React.Fragment>
      <div className={styles.colorUpsellModal}>
        <div className={styles.formSection}>
          <div className={styles.formHeadline}>Get {discountText} off a second one.</div>
          <div className={styles.formCopy}>Two is better than one. The more you shop, the more you save!</div>
          <DivButton onClick={() => setModal({ toggleState: ModalToggleState.Closed })} className={styles.closeModal} label="Close modal">
            <Icon name="close" style={{ width: '12px' }} />
          </DivButton>
          <div style={{ flex: 1 }} />
          <div className={styles.hlControls}>
            <ColorSwatches
              colors={availableVariants.map((v) => v.color)}
              activeColor={selectedVariant?.color}
              activate={(color) => setSelectedVariant(availableVariants.find((v) => v.color === color) ?? variant)}
              singleOption={availableVariants.length === 1}
              label1={true}
              label2={false}
              styleOverrides={{
                title: { fontSize: '12px' },
              }}
            />
            <div className={styles.ctaSection}>
              <ProductFormButton selectionKey={'ColorUpsellModal'} buttonType="secondary" selectionType={selectionType} variantOverride={selectedVariant} />
              <DivButton style={{ marginTop: '10px' }} onClick={() => setModal({ toggleState: ModalToggleState.Closed })} label="Close modal">
                NO THANKS
              </DivButton>
            </div>
          </div>
        </div>
        {mainPhoto && <img alt="product" className={styles.imageSection} src={sizeImage(mainPhoto.src, '660x')} />}
      </div>
    </React.Fragment>
  );
};

export default ColorUpsellModal;
